import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { getColorTheme, setColorTheme } from '../lib/theme';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Settings as SettingsIcon, 
  Key, 
  Mail, 
  Sun, 
  Moon, 
  Bell, 
  Save, 
  RotateCcw,
  Eye,
  Edit,
  Plus,
  Info,
  Check,
  Menu,
  GripVertical,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  Trash2,
  Skull,
  Loader2,
  Download,
  Clock,
  CalendarClock,
  CheckCircle2,
  XCircle,
  Play
} from 'lucide-react';
import { toast } from 'sonner';
import BackupModal from '../components/backup/BackupModal';
import BounceCodesEditor from '../components/settings/BounceCodesEditor';
import AgentSettingsPanel from '../components/settings/AgentSettingsPanel';
import PaymentMethodsSettings from '../components/settings/PaymentMethodsSettings';
import FileStorageSettings from '../components/settings/FileStorageSettings';
import StageRulesSettings from '../components/settings/StageRulesSettings';
import EmailSendersSettings from '../components/settings/EmailSendersSettings';
import ApiKeysPanel from '../components/settings/ApiKeysPanel';
import DealAliasesPanel from '../components/settings/DealAliasesPanel';
import DocumentTemplatesSettings from '../components/settings/DocumentTemplatesSettings';
import SmsTemplatesSettings from '../components/settings/SmsTemplatesSettings';
import UsersPage from './Users';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);
  
  // User preferences
  const [preferences, setPreferences] = useState({
    theme: 'light',
    notifications_enabled: true,
    email_notifications: true,
    nav_order: null
  });

  // Color theme (Original <-> Power) — persisted in localStorage, applied app-wide.
  const [colorTheme, setColorThemeState] = useState(getColorTheme());
  
  // Navigation order state
  const [navOrder, setNavOrder] = useState([]);
  
  // Password change
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordError, setPasswordError] = useState('');
  
  // Email templates (admin only)
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // System Reset (Danger Zone) state
  const [nukePreview, setNukePreview] = useState(null);
  const [nukeLoading, setNukeLoading] = useState(false);
  const [nukeConfirmText, setNukeConfirmText] = useState('');
  const [nukeResult, setNukeResult] = useState(null);

  // Post-import reconcile state
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);

  // Auto-backup state
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupStatusLoading, setBackupStatusLoading] = useState(false);
  const [backupToggling, setBackupToggling] = useState(false);
  const [backupRunning, setBackupRunning] = useState(false);
  const [backupFiles, setBackupFiles] = useState([]);

  // Syndication recompute state
  const [recomputeStatus, setRecomputeStatus] = useState(null);
  const [recomputeLoading, setRecomputeLoading] = useState(false);
  const [recomputeToggling, setRecomputeToggling] = useState(false);
  const [recomputeRunning, setRecomputeRunning] = useState(false);
  
  const isAdmin = user?.roles?.includes('admin');
  const isSuperAdmin = user?.roles?.includes('super_admin'); // 10/07/2026: users, API keys, danger zone

  // Default navigation items based on roles
  const getDefaultNavItems = () => {
    const allItems = [
      { id: 'home', label: 'Home', roles: ['admin', 'broker', 'syndicator', 'funder', 'iso', 'merchant', 'underwriter'] },
      { id: 'contacts', label: 'Contacts', roles: ['admin'] },
      { id: 'deals', label: 'Deals', roles: ['admin', 'broker', 'funder', 'underwriter'] },
      { id: 'my-deals', label: 'My Deals', roles: ['iso', 'merchant'] },
      { id: 'submit-application', label: 'Submit Application', roles: ['iso', 'broker'] },
      { id: 'my-applications', label: 'My Applications', roles: ['iso', 'broker'] },
      { id: 'commissions', label: 'Commissions', roles: ['admin', 'broker', 'iso'] },
      { id: 'reports', label: 'Reports', roles: ['admin', 'funder', 'broker'] },
      { id: 'portfolio', label: 'Portfolio', roles: ['admin', 'broker', 'funder'] },
      { id: 'syndication-dashboard', label: 'Syndication Dashboard', roles: ['admin', 'syndicator'] },
      { id: 'withdrawals', label: 'Withdrawals', roles: ['admin', 'syndicator'] },
      { id: 'settings', label: 'Settings', roles: ['admin', 'broker', 'syndicator', 'funder', 'iso', 'merchant', 'underwriter'] },
    ];
    
    // Filter items based on user roles
    return allItems.filter(item => 
      user?.roles?.some(role => item.roles.includes(role))
    );
  };

  useEffect(() => {
    fetchPreferences();
    if (isAdmin) {
      fetchTemplates();
      fetchPlaceholders();
      fetchBackupStatus();
      fetchRecomputeStatus();
    }
  }, [isAdmin]);

  // Initialize nav order when preferences load
  useEffect(() => {
    const defaultItems = getDefaultNavItems();
    if (preferences.nav_order && preferences.nav_order.length > 0) {
      const orderedItems = [];
      preferences.nav_order.forEach(id => {
        const item = defaultItems.find(i => i.id === id);
        if (item) orderedItems.push(item);
      });
      defaultItems.forEach(item => {
        if (!orderedItems.find(i => i.id === item.id)) {
          orderedItems.push(item);
        }
      });
      setNavOrder(orderedItems);
    } else {
      setNavOrder(defaultItems);
    }
  }, [preferences.nav_order, user]);

  const moveNavItem = (index, direction) => {
    const newOrder = [...navOrder];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newOrder.length) return;
    
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    setNavOrder(newOrder);
  };

  const saveNavOrder = async () => {
    try {
      const orderIds = navOrder.map(item => item.id);
      await axios.put(`${API}/settings/preferences/nav-order`, orderIds, {
        headers: getAuthHeaders()
      });
      setPreferences(prev => ({ ...prev, nav_order: orderIds }));
      // Store in localStorage for immediate Layout update
      localStorage.setItem('nav_order', JSON.stringify(orderIds));
      toast.success('Navigation order saved');
      // Trigger a page reload to apply the new order
      window.location.reload();
    } catch (error) {
      toast.error('Failed to save navigation order');
    }
  };

  const resetNavOrder = () => {
    setNavOrder(getDefaultNavItems());
  };

  // ==================== SYSTEM RESET (DANGER ZONE) ====================
  const fetchNukePreview = async () => {
    setNukeLoading(true);
    setNukeResult(null);
    try {
      const response = await axios.get(`${API}/admin/system/nuke-preview`, {
        headers: {
          ...getAuthHeaders(),
          'X-Tenant': 'live'
        }
      });
      setNukePreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch preview');
    } finally {
      setNukeLoading(false);
    }
  };

  const executeNuke = async () => {
    if (nukeConfirmText !== 'NUKE EVERYTHING') {
      toast.error('You must type "NUKE EVERYTHING" exactly to proceed');
      return;
    }
    
    setNukeLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/system/nuke?confirm=NUKE`,
        { confirm_text: nukeConfirmText },
        {
          headers: {
            ...getAuthHeaders(),
            'X-Tenant': 'live'
          }
        }
      );
      setNukeResult(response.data);
      setNukePreview(null);
      setNukeConfirmText('');
      toast.success('System reset completed successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to execute system reset');
    } finally {
      setNukeLoading(false);
    }
  };

  const resetNukeState = () => {
    setNukePreview(null);
    setNukeResult(null);
    setNukeConfirmText('');
    setReconcileResult(null);
  };

  const runPostImportReconcile = async () => {
    setReconcileLoading(true);
    setReconcileResult(null);
    try {
      const res = await axios.post(`${API}/admin/system/post-import-reconcile`, {}, { headers: getAuthHeaders() });
      setReconcileResult(res.data);
      toast.success('Post-import reconciliation complete');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reconciliation failed');
    } finally {
      setReconcileLoading(false);
    }
  };

  const fetchBackupStatus = async () => {
    setBackupStatusLoading(true);
    try {
      const statusRes = await axios.get(`${API}/backup/status`, { headers: getAuthHeaders() });
      setBackupStatus(statusRes.data);
    } catch (err) {
      console.error('Failed to fetch backup status', err);
    }
    try {
      const filesRes = await axios.get(`${API}/backup/files`, { headers: getAuthHeaders() });
      setBackupFiles(filesRes.data || []);
    } catch (err) {
      console.error('Failed to fetch backup files', err);
    }
    setBackupStatusLoading(false);
  };

  const handleBackupToggle = async (enabled) => {
    setBackupToggling(true);
    try {
      const res = await axios.put(`${API}/backup/settings`, { enabled }, { headers: getAuthHeaders() });
      setBackupStatus(prev => ({ ...prev, enabled: res.data.enabled, next_run_at: res.data.next_run_at }));
      toast.success(enabled ? 'Auto-backup enabled' : 'Auto-backup disabled');
    } catch {
      toast.error('Failed to update backup settings');
    } finally {
      setBackupToggling(false);
    }
  };

  const handleRunBackupNow = async () => {
    setBackupRunning(true);
    try {
      await axios.post(`${API}/backup/run-now`, {}, { headers: getAuthHeaders() });
      toast.success('Backup completed successfully');
      await fetchBackupStatus();
    } catch {
      toast.error('Backup failed — check server logs');
    } finally {
      setBackupRunning(false);
    }
  };

  const fmtBackupTime = (iso) => {
    if (!iso) return 'Never';
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
      });
    } catch { return iso; }
  };

  // Syndication recompute handlers
  const fetchRecomputeStatus = async () => {
    setRecomputeLoading(true);
    try {
      const res = await axios.get(`${API}/recompute/status`, { headers: getAuthHeaders() });
      setRecomputeStatus(res.data);
      // If a run is already in flight (started earlier / another tab), attach to it.
      if (res.data.running) {
        pollRecomputeUntilDone();
      }
    } catch (err) {
      console.error('Failed to fetch recompute status', err);
    }
    setRecomputeLoading(false);
  };

  const handleRecomputeToggle = async (enabled) => {
    setRecomputeToggling(true);
    try {
      const res = await axios.put(`${API}/recompute/settings`, { enabled }, { headers: getAuthHeaders() });
      setRecomputeStatus(prev => ({ ...prev, enabled: res.data.enabled, next_run_at: res.data.next_run_at }));
      toast.success(enabled ? 'Daily recompute enabled' : 'Daily recompute disabled');
    } catch {
      toast.error('Failed to update recompute settings');
    } finally {
      setRecomputeToggling(false);
    }
  };

  // Poll /recompute/status until the background run finishes, then announce.
  // pollingRef guarantees a single loop even if mount + click both trigger it.
  const pollingRef = useRef(false);
  const pollRecomputeUntilDone = async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setRecomputeRunning(true);
    try {
      for (;;) {
        await new Promise((r) => setTimeout(r, 4000));
        const res = await axios.get(`${API}/recompute/status`, { headers: getAuthHeaders() });
        setRecomputeStatus(res.data);
        if (!res.data.running) {
          const skipped = res.data.needs_review?.count;
          if (res.data.last_run_status === 'error') {
            toast.error(`Recompute finished with errors — ${res.data.last_run_error || 'see status panel'}`);
          } else {
            toast.success(`Recompute complete — ${res.data.deals_processed} deals, ${res.data.syndications_updated} syndications updated${skipped ? `, ${skipped} skipped for review` : ''}`);
          }
          break;
        }
      }
    } catch {
      toast.error('Lost contact with the recompute status — reload the page to check');
    } finally {
      pollingRef.current = false;
      setRecomputeRunning(false);
    }
  };

  const handleRunRecomputeNow = async () => {
    setRecomputeRunning(true);
    try {
      // Returns immediately; the run continues server-side (no proxy timeout).
      const res = await axios.post(`${API}/recompute/run-now`, {}, { headers: getAuthHeaders() });
      if (res.data.status === 'already_running') {
        toast.info('A recompute is already running — showing its progress');
      }
      await pollRecomputeUntilDone();
    } catch {
      toast.error('Could not start recompute — check server logs');
      setRecomputeRunning(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API}/settings/preferences`, {
        headers: getAuthHeaders()
      });
      setPreferences(response.data);
      
      // Apply theme
      if (response.data.theme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/settings/email-templates`, {
        headers: getAuthHeaders()
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchPlaceholders = async () => {
    try {
      const response = await axios.get(`${API}/settings/email-templates/placeholders`, {
        headers: getAuthHeaders()
      });
      setPlaceholders(response.data);
    } catch (error) {
      console.error('Failed to fetch placeholders:', error);
    }
  };

  const handlePreferenceChange = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    
    // Apply theme immediately
    if (key === 'theme') {
      if (value === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
    
    // Save to backend
    try {
      await axios.put(`${API}/settings/preferences`, newPrefs, {
        headers: getAuthHeaders()
      });
      toast.success('Preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (passwordForm.new_password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/settings/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        headers: getAuthHeaders()
      });
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleTemplateEdit = (template) => {
    setEditingTemplate({ ...template });
    setTemplateDialogOpen(true);
  };

  const handleTemplateSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/email-templates/${editingTemplate.id}`, editingTemplate, {
        headers: getAuthHeaders()
      });
      toast.success('Template saved successfully');
      setTemplateDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleTemplateToggle = async (template) => {
    const updated = { ...template, is_active: !template.is_active };
    try {
      await axios.put(`${API}/settings/email-templates/${template.id}`, updated, {
        headers: getAuthHeaders()
      });
      setTemplates(prev => prev.map(t => t.id === template.id ? { ...t, is_active: updated.is_active } : t));
      toast.success(`${template.name} ${updated.is_active ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handleResetTemplates = async () => {
    if (!window.confirm('Reset all email templates to defaults? This cannot be undone.')) return;
    
    try {
      await axios.post(`${API}/settings/email-templates/reset-defaults`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Templates reset to defaults');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to reset templates');
    }
  };

  const getTemplateTypeBadge = (type) => {
    const colors = {
      'welcome': 'bg-blue-100 text-blue-800',
      'approval': 'bg-green-100 text-green-800',
      'rejection': 'bg-red-100 text-red-800',
      'status_change': 'bg-yellow-100 text-yellow-800',
      'password_reset': 'bg-purple-100 text-purple-800',
      'bounce_notice': 'bg-orange-100 text-orange-800',
      'outreach': 'bg-amber-100 text-amber-800'
    };
    return <Badge className={colors[type] || 'bg-gray-100'}>{type}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 sm:px-0 max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <SettingsIcon className="h-8 w-8" />
            Settings
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Manage your account and preferences</p>
          {isAdmin && (
            <div className="mt-3">
              <Button
                onClick={() => setBackupOpen(true)}
                className="bg-blue-900 hover:bg-blue-800 text-white"
                data-testid="settings-backup-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Backup Data
              </Button>
            </div>
          )}
        </div>

        <Tabs defaultValue="account" className="space-y-6">
          {/* 10/07 (Josh: "i dont see it in the settings"): the admin strip is now 14 tabs. The old
              fixed 6-column grid kept the list at a fixed height, so the third row (Deal Aliases,
              Document Templates) rendered out of view. Wrap instead of clipping. */}
          <TabsList className={isAdmin ? "flex flex-wrap h-auto w-full justify-start gap-1" : "grid w-full grid-cols-3"}>
            <TabsTrigger value="account">Account</TabsTrigger>
            <TabsTrigger value="preferences">Preferences</TabsTrigger>
            <TabsTrigger value="navigation">Navigation</TabsTrigger>
            {isSuperAdmin && <TabsTrigger value="backup">Auto-Backup</TabsTrigger>}
            {isSuperAdmin && <TabsTrigger value="recompute">Syndication Recompute</TabsTrigger>}
            {isSuperAdmin && <TabsTrigger value="file-storage">File Storage</TabsTrigger>}
            {isAdmin && <TabsTrigger value="email-templates">Email Templates</TabsTrigger>}
            {isAdmin && <TabsTrigger value="email-senders">Email Senders</TabsTrigger>}
            {isSuperAdmin && <TabsTrigger value="users">Users</TabsTrigger>}
            {isSuperAdmin && <TabsTrigger value="api-keys">API Keys</TabsTrigger>}
            {isAdmin && <TabsTrigger value="bounce-codes">Bounce Codes</TabsTrigger>}
            {isSuperAdmin && <TabsTrigger value="ai-assistant">AI Assistant</TabsTrigger>}
            {isAdmin && <TabsTrigger value="payment-methods">Payment Methods</TabsTrigger>}
            {isAdmin && <TabsTrigger value="stage-rules">Stage Rules</TabsTrigger>}
            {isAdmin && <TabsTrigger value="deal-aliases">Deal Aliases</TabsTrigger>}
            {isSuperAdmin && <TabsTrigger value="document-templates">Document Templates</TabsTrigger>}
            {isAdmin && <TabsTrigger value="sms-templates">SMS Templates</TabsTrigger>}
            {isSuperAdmin && (
              <TabsTrigger value="danger-zone" className="text-red-600 data-[state=active]:text-red-700">
                <AlertTriangle className="h-4 w-4 mr-1" />
                Danger Zone
              </TabsTrigger>
            )}
          </TabsList>

          {/* Account Tab */}
          <TabsContent value="account">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  Change Password
                </CardTitle>
                <CardDescription>
                  Update your password to keep your account secure
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                  <div>
                    <Label htmlFor="current_password">Current Password</Label>
                    <Input
                      id="current_password"
                      type="password"
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="new_password">New Password</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                      required
                      minLength={8}
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                      required
                    />
                  </div>
                  {passwordError && (
                    <p className="text-sm text-red-600">{passwordError}</p>
                  )}
                  <Button type="submit" disabled={saving}>
                    {saving ? 'Saving...' : 'Change Password'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Preferences Tab */}
          <TabsContent value="preferences">
            <div className="space-y-6">
              {/* Theme */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {preferences.theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                    Appearance
                  </CardTitle>
                  <CardDescription>
                    Customize how the application looks
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Theme</p>
                      <p className="text-sm text-gray-500">Choose between light and dark mode</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Sun className="h-4 w-4 text-yellow-500" />
                      <Switch
                        checked={preferences.theme === 'dark'}
                        onCheckedChange={(checked) => handlePreferenceChange('theme', checked ? 'dark' : 'light')}
                      />
                      <Moon className="h-4 w-4 text-blue-500" />
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-6 pt-6 border-t">
                    <div>
                      <p className="font-medium">Color theme</p>
                      <p className="text-sm text-gray-500">Switch between the classic look, the Power theme, and the MCA Dark theme</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant={colorTheme === 'original' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setColorThemeState(setColorTheme('original'))}
                      >
                        Original
                      </Button>
                      <Button
                        type="button"
                        variant={colorTheme === 'power' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setColorThemeState(setColorTheme('power'))}
                      >
                        Power
                      </Button>
                      <Button
                        type="button"
                        variant={colorTheme === 'mcadark' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setColorThemeState(setColorTheme('mcadark'))}
                      >
                        MCA Dark
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5" />
                    Notifications
                  </CardTitle>
                  <CardDescription>
                    Manage your notification preferences
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">In-App Notifications</p>
                      <p className="text-sm text-gray-500">Receive notifications within the app</p>
                    </div>
                    <Switch
                      checked={preferences.notifications_enabled}
                      onCheckedChange={(checked) => handlePreferenceChange('notifications_enabled', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Email Notifications</p>
                      <p className="text-sm text-gray-500">Receive important updates via email</p>
                    </div>
                    <Switch
                      checked={preferences.email_notifications}
                      onCheckedChange={(checked) => handlePreferenceChange('email_notifications', checked)}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Navigation Tab */}
          <TabsContent value="navigation">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Menu className="h-5 w-5" />
                      Navigation Menu Order
                    </CardTitle>
                    <CardDescription>
                      Customize the order of items in your navigation menu
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={resetNavOrder}>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset
                    </Button>
                    <Button onClick={saveNavOrder}>
                      <Save className="h-4 w-4 mr-2" />
                      Save Order
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {navOrder.map((item, index) => (
                    <div 
                      key={item.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border"
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="h-5 w-5 text-gray-400" />
                        <span className="font-medium">{item.label}</span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveNavItem(index, 'up')}
                          disabled={index === 0}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveNavItem(index, 'down')}
                          disabled={index === navOrder.length - 1}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  Use the arrows to reorder menu items. Changes will apply after saving.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Auto-Backup Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="backup">
              <div className="space-y-4">
                {/* Status Card */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <CalendarClock className="h-5 w-5" />
                          Nightly Auto-Backup
                        </CardTitle>
                        <CardDescription>
                          Automatically exports Deals, Transactions, and Syndications every night at 2:00 AM UTC.
                          The last 7 days of backups are kept on the server.
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-3">
                        {backupToggling && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
                        <Switch
                          checked={backupStatus?.enabled !== false}
                          onCheckedChange={handleBackupToggle}
                          disabled={backupToggling || backupStatusLoading}
                          data-testid="auto-backup-toggle"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {backupStatus?.enabled !== false ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {backupStatusLoading ? (
                      <div className="flex items-center gap-2 text-gray-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Loading backup status…</span>
                      </div>
                    ) : backupStatus ? (
                      <>
                        {/* Status indicator */}
                        {backupStatus.last_run_status && (
                          <div
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
                              ${backupStatus.last_run_status === 'success'
                                ? 'bg-green-50 border border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400'
                                : 'bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'}`}
                            data-testid="backup-status-indicator"
                          >
                            {backupStatus.last_run_status === 'success'
                              ? <CheckCircle2 className="h-4 w-4 shrink-0" />
                              : <XCircle className="h-4 w-4 shrink-0" />}
                            {backupStatus.last_run_status === 'success'
                              ? `Last backup successful — ${fmtBackupTime(backupStatus.last_run_at)}`
                              : `Last backup failed — ${fmtBackupTime(backupStatus.last_run_at)}: ${backupStatus.last_run_error || 'unknown error'}`}
                          </div>
                        )}

                        {/* Timestamps grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                            <Clock className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">Last Backup</p>
                              <p className="text-sm font-medium text-gray-800 dark:text-gray-200" data-testid="last-backup-time">
                                {fmtBackupTime(backupStatus.last_run_at)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                            <CalendarClock className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">Next Backup</p>
                              <p className="text-sm font-medium text-gray-800 dark:text-gray-200" data-testid="next-backup-time">
                                {backupStatus.enabled !== false ? fmtBackupTime(backupStatus.next_run_at) : 'Disabled'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-gray-500">No backup has run yet.</p>
                    )}

                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-3 pt-1">
                      <Button
                        variant="outline"
                        onClick={handleRunBackupNow}
                        disabled={backupRunning}
                        data-testid="run-backup-now-btn"
                      >
                        {backupRunning
                          ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Running…</>
                          : <><Play className="h-4 w-4 mr-2" />Run Backup Now</>}
                      </Button>
                      <Button
                        onClick={() => setBackupOpen(true)}
                        className="bg-blue-900 hover:bg-blue-800 text-white"
                        data-testid="backup-download-last-btn"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download Last Backup
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Schedule info */}
                <Card className="border-blue-100 dark:border-blue-900/50">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                      <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                        <p><strong>Schedule:</strong> Daily at 2:00 AM UTC</p>
                        <p><strong>Files exported:</strong> deals_export_YYYY-MM-DD.csv, transactions_export_YYYY-MM-DD.csv, syndications_backup_YYYY-MM-DD.csv</p>
                        <p><strong>Retention:</strong> Last 7 days of backups are kept; older files are automatically deleted</p>
                        <p><strong>Storage:</strong> Files are saved to <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">/app/backups/</code> on the server</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Stored backup files */}
                {backupFiles.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Stored Backup Files</CardTitle>
                      <CardDescription>Download any previously saved backup file directly from the server.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {backupFiles.filter(f => f.row_count > 0).map(f => (
                          <div key={f.filename} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="text-sm">
                                <p className="font-medium text-gray-800 dark:text-gray-200 truncate">{f.filename}</p>
                                <p className="text-xs text-gray-500">{f.row_count.toLocaleString()} rows · {(f.size_bytes / 1024).toFixed(0)} KB · {fmtBackupTime(f.modified_at)}</p>
                              </div>
                            </div>
                            <a
                              href={`${API}/backup/files/${encodeURIComponent(f.filename)}`}
                              download={f.filename}
                              onClick={e => {
                                e.preventDefault();
                                const a = document.createElement('a');
                                a.href = `${API}/backup/files/${encodeURIComponent(f.filename)}`;
                                a.download = f.filename;
                                const headers = getAuthHeaders();
                                fetch(a.href, { headers })
                                  .then(r => r.blob())
                                  .then(blob => {
                                    const url = URL.createObjectURL(blob);
                                    a.href = url;
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                    URL.revokeObjectURL(url);
                                  });
                              }}
                              className="ml-3 shrink-0"
                              data-testid={`download-backup-${f.filename}`}
                            >
                              <Button variant="outline" size="sm">
                                <Download className="h-3.5 w-3.5 mr-1.5" />
                                Download
                              </Button>
                            </a>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          )}

          {isSuperAdmin && (
            <TabsContent value="file-storage"><FileStorageSettings /></TabsContent>
          )}

          {/* Syndication Recompute Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="recompute">
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">Daily Syndication Recompute</CardTitle>
                        <p className="text-sm text-gray-500 mt-1">
                          Recalculates running_collection and available_balance from actual transactions for all deals. Runs daily at 6:00 AM UTC. Never modifies total_withdrawals.
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={recomputeStatus?.enabled ?? true}
                            onChange={e => handleRecomputeToggle(e.target.checked)}
                            disabled={recomputeToggling}
                            className="sr-only peer"
                            data-testid="recompute-toggle"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                        </label>
                        <span className="text-sm font-medium">{recomputeStatus?.enabled ? 'Enabled' : 'Disabled'}</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {recomputeLoading ? (
                      <p className="text-sm text-gray-400">Loading status...</p>
                    ) : recomputeStatus ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Last Run</p>
                          <p className="font-medium">{fmtBackupTime(recomputeStatus.last_run_at)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Status</p>
                          <p className={`font-medium ${recomputeStatus.last_run_status === 'success' ? 'text-green-600' : recomputeStatus.last_run_status === 'error' ? 'text-red-600' : recomputeStatus.last_run_status === 'needs_review' ? 'text-amber-600' : 'text-gray-500'}`}>
                            {recomputeStatus.last_run_status === 'needs_review' ? 'Needs review' : (recomputeStatus.last_run_status || 'Never run')}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Deals Processed</p>
                          <p className="font-medium">{recomputeStatus.deals_processed ?? '-'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Next Run</p>
                          <p className="font-medium">{recomputeStatus.enabled ? fmtBackupTime(recomputeStatus.next_run_at) : 'Disabled'}</p>
                        </div>
                      </div>
                    ) : null}
                    {recomputeStatus?.needs_review?.count > 0 && (
                      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 space-y-1" data-testid="recompute-needs-review">
                        <p className="font-medium">
                          {recomputeStatus.needs_review.count} deal{recomputeStatus.needs_review.count === 1 ? '' : 's'} skipped — accounting records need review before their positions can be recomputed
                        </p>
                        <p className="text-amber-700">
                          {Object.entries(recomputeStatus.needs_review.issue_counts || {})
                            .map(([code, n]) => `${code.replace(/_/g, ' ')}: ${n}`)
                            .join(' · ') || 'accounting review required'}
                        </p>
                        {recomputeStatus.needs_review.deals?.length > 0 && (
                          <details>
                            <summary className="cursor-pointer text-amber-700 hover:text-amber-900">Show deals</summary>
                            <p className="mt-1 break-words text-xs text-amber-700">
                              {recomputeStatus.needs_review.deals.map((d) => `#${d}`).join(', ')}
                            </p>
                          </details>
                        )}
                      </div>
                    )}
                    {recomputeStatus?.last_run_error && (
                      <p className="text-sm text-red-500">Error: {recomputeStatus.last_run_error}</p>
                    )}
                    <Button
                      onClick={handleRunRecomputeNow}
                      disabled={recomputeRunning}
                      variant="outline"
                      data-testid="run-recompute-now-btn"
                    >
                      {recomputeRunning
                        ? (recomputeStatus?.progress?.total
                            ? `Recomputing ${recomputeStatus.progress.done}/${recomputeStatus.progress.total} deals…`
                            : 'Recomputing…')
                        : 'Run Recompute Now'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Users Tab (Admin Only) — moved here from the main nav 09/17 */}
          {isAdmin && (
            <TabsContent value="users">
              <UsersPage embedded />
            </TabsContent>
          )}

          {/* API Keys Tab (Admin Only) — 08/12 external integrations */}
          {isAdmin && (
            <TabsContent value="api-keys">
              <ApiKeysPanel getAuthHeaders={getAuthHeaders} />
            </TabsContent>
          )}

          {/* Email Templates Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="email-templates">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        Email Templates
                      </CardTitle>
                      <CardDescription>
                        Customize email templates sent to users and applicants
                      </CardDescription>
                    </div>
                    <Button variant="outline" onClick={handleResetTemplates}>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset to Defaults
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        className="border rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <h3 className="font-medium">{template.name}</h3>
                            {getTemplateTypeBadge(template.template_type)}
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <Switch
                                checked={template.is_active}
                                onCheckedChange={() => handleTemplateToggle(template)}
                                data-testid={`template-toggle-${template.template_type}`}
                              />
                              <span className={`text-xs font-medium ${template.is_active ? 'text-green-700' : 'text-gray-500'}`}>
                                {template.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleTemplateEdit(template)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <strong>Subject:</strong> {template.subject}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Bounce Codes Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="bounce-codes">
              <BounceCodesEditor getAuthHeaders={getAuthHeaders} />
            </TabsContent>
          )}

          {/* Deal Aliases (Admin Only) — relocated from the retired Data Center page (Josh 10/07).
              The alias table is still read by Daily Import / Universal Import matching. */}
          {isAdmin && (
            <TabsContent value="deal-aliases">
              <DealAliasesPanel />
            </TabsContent>
          )}

          {/* Document Templates (Admin Only) — letter wording + funding-company branding / wire details (Josh 10/07) */}
          {isAdmin && (
            <TabsContent value="document-templates">
              <DocumentTemplatesSettings />
            </TabsContent>
          )}

          {/* SMS Templates (Admin) — Collections Center texts (10/12) */}
          {isAdmin && (
            <TabsContent value="sms-templates">
              <SmsTemplatesSettings />
            </TabsContent>
          )}

          {/* AI Assistant Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="ai-assistant">
              <AgentSettingsPanel />
            </TabsContent>
          )}

          {/* Payment Methods Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="payment-methods">
              <PaymentMethodsSettings />
            </TabsContent>
          )}

          {/* Email Senders (Admin) — Josh 10/12: which mailbox each kind of email goes out from */}
          {isAdmin && (
            <TabsContent value="email-senders">
              <EmailSendersSettings />
            </TabsContent>
          )}

          {/* Stage Rules (Admin) — Josh 10/12: automatic Funded ⇄ Payment Issues scenarios */}
          {isAdmin && (
            <TabsContent value="stage-rules">
              <StageRulesSettings />
            </TabsContent>
          )}

          {/* Danger Zone Tab (Admin Only) */}
          {isAdmin && (
            <TabsContent value="danger-zone">
              <Card className="border-red-200 dark:border-red-900">
                <CardHeader className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-900">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-full">
                      <Skull className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <CardTitle className="text-red-700 dark:text-red-400 flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5" />
                        System Reset (NUKE)
                      </CardTitle>
                      <CardDescription className="text-red-600/80 dark:text-red-400/80">
                        Permanently delete ALL deal data and reset the system. This action cannot be undone.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  {!nukePreview && !nukeResult && (
                    <div className="space-y-6">
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/50 rounded-lg p-4">
                        <h4 className="font-semibold text-yellow-800 dark:text-yellow-300 flex items-center gap-2 mb-2">
                          <AlertTriangle className="h-5 w-5" />
                          What will be deleted:
                        </h4>
                        <ul className="list-disc list-inside text-sm text-yellow-700 dark:text-yellow-400 space-y-1">
                          <li>All deals, transactions, and syndications</li>
                          <li>All withdrawals and upload sessions</li>
                          <li>All audit logs and data health records</li>
                        </ul>
                        <h4 className="font-semibold text-green-800 dark:text-green-300 flex items-center gap-2 mt-4 mb-2">
                          <Check className="h-5 w-5" />
                          What will be preserved:
                        </h4>
                        <ul className="list-disc list-inside text-sm text-green-700 dark:text-green-400 space-y-1">
                          <li>All user accounts</li>
                          <li>Contacts (with deal links preserved)</li>
                          <li>Wire instructions</li>
                          <li>UW Placeholder dictionary</li>
                          <li>Refinance linkage (auto-backed up, restored after re-import)</li>
                        </ul>
                      </div>
                      
                      <Button 
                        variant="destructive"
                        onClick={fetchNukePreview}
                        disabled={nukeLoading}
                        className="w-full bg-red-600 hover:bg-red-700"
                        data-testid="nuke-preview-btn"
                      >
                        {nukeLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Loading Preview...
                          </>
                        ) : (
                          <>
                            <Eye className="h-4 w-4 mr-2" />
                            Preview Data to be Deleted
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {nukePreview && (
                    <div className="space-y-6">
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/50 rounded-lg p-4">
                        <h4 className="font-semibold text-red-800 dark:text-red-300 mb-3">
                          Data to be PERMANENTLY DELETED:
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {Object.entries(nukePreview.collections_to_wipe || {}).map(([key, count]) => (
                            <div key={key} className="flex justify-between bg-white dark:bg-gray-800 p-2 rounded border">
                              <span className="text-gray-600 dark:text-gray-400 capitalize">{key.replace(/_/g, ' ')}</span>
                              <span className="font-mono font-bold text-red-600">{count.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 pt-4 border-t border-red-200 dark:border-red-900/50">
                          <div className="flex justify-between text-lg font-semibold">
                            <span className="text-red-800 dark:text-red-300">Total Documents:</span>
                            <span className="text-red-600">{nukePreview.total_documents?.toLocaleString() || 0}</span>
                          </div>
                          <div className="flex justify-between mt-2">
                            <span className="text-red-800 dark:text-red-300">Users to Delete:</span>
                            <span className="text-red-600 font-mono">{nukePreview.users?.to_delete || 0}</span>
                          </div>
                          <div className="flex justify-between mt-2">
                            <span className="text-green-800 dark:text-green-300">Protected Admin:</span>
                            <span className="text-green-600 font-mono">{nukePreview.users?.protected}</span>
                          </div>
                          <div className="flex justify-between mt-2">
                            <span className="text-blue-800 dark:text-blue-300">Contacts (kept but unlinked):</span>
                            <span className="text-blue-600 font-mono">{nukePreview.contacts?.total || 0}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-900 dark:bg-black rounded-lg p-4 border-2 border-red-600">
                        <Label className="text-white mb-2 block">
                          Type <code className="bg-red-600 px-2 py-1 rounded text-white font-bold">NUKE EVERYTHING</code> to confirm:
                        </Label>
                        <Input
                          value={nukeConfirmText}
                          onChange={(e) => setNukeConfirmText(e.target.value)}
                          placeholder="NUKE EVERYTHING"
                          className="bg-white dark:bg-gray-800 font-mono text-lg text-center border-2 border-gray-300 dark:border-gray-600"
                          data-testid="nuke-confirm-input"
                        />
                      </div>

                      <div className="flex gap-3">
                        <Button 
                          variant="outline" 
                          onClick={resetNukeState}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={executeNuke}
                          disabled={nukeLoading || nukeConfirmText !== 'NUKE EVERYTHING'}
                          className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50"
                          data-testid="nuke-execute-btn"
                        >
                          {nukeLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Executing...
                            </>
                          ) : (
                            <>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Execute NUKE & RESET
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {nukeResult && (
                    <div className="space-y-6">
                      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/50 rounded-lg p-4">
                        <h4 className="font-semibold text-green-800 dark:text-green-300 flex items-center gap-2 mb-3">
                          <Check className="h-5 w-5" />
                          System Reset Complete
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {Object.entries(nukeResult.deleted_counts || {}).map(([key, count]) => (
                            <div key={key} className="flex justify-between bg-white dark:bg-gray-800 p-2 rounded border">
                              <span className="text-gray-600 dark:text-gray-400 capitalize">{key.replace(/_/g, ' ')}</span>
                              <span className="font-mono text-green-600">{typeof count === 'number' ? count.toLocaleString() : count}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-900/50 space-y-2">
                          <div className="flex justify-between">
                            <span>Users Deleted:</span>
                            <span className="font-mono">{nukeResult.users_deleted_count}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Contacts Unlinked:</span>
                            <span className="font-mono">{nukeResult.contacts_unlinked_count}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Duration:</span>
                            <span className="font-mono">{nukeResult.duration_ms}ms</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Audit ID:</span>
                            <span className="font-mono text-xs">{nukeResult.audit_id}</span>
                          </div>
                        </div>
                      </div>
                      
                      <Button 
                        variant="outline" 
                        onClick={resetNukeState}
                        className="w-full"
                      >
                        Done
                      </Button>
                    </div>
                  )}

                  {/* Post-Import Reconcile */}
                  <div className="border-t pt-6 mt-6">
                    <h4 className="font-semibold mb-2">Post-Import Reconciliation</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      After re-importing deals, transactions, and syndications, run this to restore refi linkage, reconcile chains, and verify calculations on key deals.
                    </p>
                    <Button
                      variant="outline"
                      onClick={runPostImportReconcile}
                      disabled={reconcileLoading}
                      data-testid="post-import-reconcile-btn"
                    >
                      {reconcileLoading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running...</>
                      ) : (
                        'Run Post-Import Reconcile'
                      )}
                    </Button>

                    {reconcileResult && (
                      <div className="mt-4 space-y-3" data-testid="reconcile-result">
                        <div className={`p-3 rounded-lg border ${reconcileResult.overall === 'PASS' ? 'bg-green-950/40 border-green-800' : 'bg-red-950/40 border-red-800'}`}>
                          <span className={`font-semibold ${reconcileResult.overall === 'PASS' ? 'text-green-300' : 'text-red-300'}`}>
                            Overall: {reconcileResult.overall}
                          </span>
                        </div>
                        {reconcileResult.steps?.map((step, idx) => (
                          <div key={idx} className="text-sm bg-muted/50 rounded-lg p-3">
                            <div className="flex justify-between items-center mb-1">
                              <span className="font-medium capitalize">{step.step.replace(/_/g, ' ')}</span>
                              <span className={`text-xs font-mono ${step.status === 'PASS' || step.status === 'DONE' ? 'text-green-400' : step.status === 'SKIP' ? 'text-yellow-400' : 'text-red-400'}`}>{step.status}</span>
                            </div>
                            {step.details && (
                              <div className="mt-2 space-y-1">
                                {step.details.map((d, i) => (
                                  <div key={i} className="flex justify-between text-xs bg-background/50 rounded px-2 py-1">
                                    <span>Deal {d.deal_id}</span>
                                    <span className={d.status === 'PASS' ? 'text-green-400' : 'text-red-400'}>
                                      {d.status} — paid ${d.payments_made?.toLocaleString()} / bal ${d.balance?.toLocaleString()}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {step.restored_deals != null && <p className="text-xs text-muted-foreground mt-1">Restored: {step.restored_deals} deals, {step.groups_restored} groups</p>}
                            {step.groups_created != null && <p className="text-xs text-muted-foreground mt-1">Linked: {step.deals_updated} deals, {step.groups_created} groups, {step.status_fixed} statuses fixed</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Template Edit Dialog */}
        <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Email Template</DialogTitle>
            </DialogHeader>
            {editingTemplate && (
              <div className="space-y-4">
                <div>
                  <Label>Template Name</Label>
                  <Input
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Subject Line</Label>
                  <Input
                    value={editingTemplate.subject}
                    onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Email Body</Label>
                  <Textarea
                    rows={12}
                    value={editingTemplate.body}
                    onChange={(e) => setEditingTemplate({...editingTemplate, body: e.target.value})}
                    className="font-mono text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingTemplate.is_active}
                    onCheckedChange={(checked) => setEditingTemplate({...editingTemplate, is_active: checked})}
                  />
                  <Label>Template Active</Label>
                </div>
                
                {/* Placeholders Reference */}
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <Info className="h-4 w-4" />
                    Available Placeholders
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {placeholders[editingTemplate.template_type]?.map((p) => (
                      <div key={p.placeholder} className="flex items-start gap-2">
                        <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded text-xs">
                          {p.placeholder}
                        </code>
                        <span className="text-gray-600 dark:text-gray-400 text-xs">
                          {p.description}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleTemplateSave} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Template'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
      <BackupModal
        open={backupOpen}
        onClose={() => setBackupOpen(false)}
        getAuthHeaders={getAuthHeaders}
      />
    </Layout>
  );
};

export default Settings;
